/* eslint-disable react-hooks/exhaustive-deps */
import { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, MenuItem, TextField } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { Iconify } from 'src/components';
import { STEP_BADGE, SECTION_CARD, chipStyle, SECTION_HEADER_LABEL, SECTION_ICON_COLOR } from 'src/styles/stepFormStyles';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import AssignedToLead from 'src/components/AssignedToLead';
import { useSelector, dispatch } from 'src/redux/store';
import { selectFollowLead, selectTableRowLead } from 'src/redux/selectors/leadSelectors';
// ContactRound imports removed — note path handles count increment
import { closeWhatsappConversationWithReason } from 'src/redux/slices/whatsappV2';
import { callApi } from 'src/utils/apiCall';
import { selectMenuItemStyle } from 'src/styles/DialogueTabs';

const REMINDER_PRESETS = [
    { label: '2 hours', key: '2hours', getDate: () => new Date(Date.now() + 2 * 3600000) },
    { label: 'Tomorrow 9am', key: 'tomorrow', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
    { label: '1 week', key: 'week', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(9, 0, 0, 0); return d; } },
    { label: '2 weeks', key: 'twoweeks', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 14); d.setHours(9, 0, 0, 0); return d; } },
    { label: 'Custom', key: 'custom', getDate: null },
    { label: 'No reminder', key: 'none', getDate: null },
];

// eslint-disable-next-line no-unused-vars
const WhatsAppFollowUpStep = forwardRef(({ handleNext, handleClose, onWhatsAppClose }, ref) => {
    const { enqueueSnackbar } = useSnackbar();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const followLead = useSelector(selectFollowLead);
    const tableRowLead = useSelector(selectTableRowLead);
    const { reminderMethod } = useSelector((state) => state.schema);
    const whatsAppThread = useSelector((state) => state.whatsappV2?.whatsAppChatMetaData?.thread);

    const [calledConfirmed, setCalledConfirmed] = useState(null);
    const [selectedCallOptions, setSelectedCallOptions] = useState([]);
    const [callNote, setCallNote] = useState('');
    const [activePreset, setActivePreset] = useState(null);
    const [assignToState, setAssignToState] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const leadId = followLead?.id;
    const treatmentId = tableRowLead?.treatment_id || tableRowLead?.lead_treatment_id || followLead?.lead_treatments?.[0]?.id;

    const methods = useForm({
        defaultValues: { reminderDateTime: null, reminder: 'WHATSAPP', notes: '' },
    });
    const { handleSubmit, control, setValue, getValues, watch } = methods;
    const reminderDateTime = watch('reminderDateTime');
    const notes = watch('notes');

    useEffect(() => {
        const preset = REMINDER_PRESETS.find(p => p.key === activePreset);
        if (preset?.getDate) setValue('reminderDateTime', preset.getDate());
    }, [activePreset]);

    const onSubmit = async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            const backendOutcome = calledConfirmed ? 'SPOKE_NOT_BOOKED' : 'UNABLE_TO_CONTACT';
            const callInfo = calledConfirmed && callNote ? `Call: ${callNote}` : '';
            const combinedNotes = [callInfo, notes].filter(Boolean).join(' | ') || '';
            if (leadId && practiceId) {
                // Create note — the note path handles count increment via contacted_via
                await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                    note: backendOutcome === 'SPOKE_NOT_BOOKED' ? 'FOLLOW_UP' : 'UNABLE_TO_CONTACT',
                    additional_notes: combinedNotes || 'WhatsApp follow up',
                    contacted_via: calledConfirmed ? 'PHONE' : 'WHATSAPP',
                    lead: leadId,
                    ...(treatmentId ? { treatment: treatmentId } : {}),
                });
            }

            if (activePreset && activePreset !== 'none' && reminderDateTime && leadId && practiceId) {
                await callApi(`/api/v1/practices/${practiceId}/reminders/`, 'post', {
                    method: getValues('reminder') || 'WHATSAPP',
                    datetime: `${moment(reminderDateTime).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
                    note: notes || 'Follow up - WhatsApp',
                    is_completed: false, group: 'FOLLOW_UP', lead: leadId,
                    ...(treatmentId ? { lead_treatment: treatmentId } : {}),
                    assignees: assignToState?.map(el => el?.id).filter(Boolean) || [],
                });
            }

            const threadId = whatsAppThread?.id;
            if (threadId && leadId) dispatch(closeWhatsappConversationWithReason(threadId, 'Follow Up', leadId));
            if (onWhatsAppClose) onWhatsAppClose('Follow Up');

            enqueueSnackbar('Follow up saved', { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
            handleClose('finish');
        } catch (err) {
            console.error('[WhatsAppFollowUpStep] submit error:', err);
            enqueueSnackbar('Failed to save follow up. Please try again.', { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
        } finally {
            setIsSaving(false);
        }
    };

    const isValid = calledConfirmed !== null && (activePreset === 'none' || (activePreset && (activePreset !== 'custom' || reminderDateTime)));

    useImperativeHandle(ref, () => ({
        async submit() {
            if (!isValid) {
                enqueueSnackbar(calledConfirmed === null ? 'Please select a contact method' : 'Please select a reminder option', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                return;
            }
            await handleSubmit(onSubmit)();
        },
    }), [calledConfirmed, activePreset, assignToState, isSaving, isValid, onSubmit, handleSubmit]);

    const BADGE = STEP_BADGE;
    const CARD = SECTION_CARD;
    const CHIP = chipStyle;

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ px: 3, py: 2 }}>

                {/* Step 1: Contact Method */}
                <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                        <Box sx={BADGE}>1</Box>
                        <Iconify icon="eva:phone-fill" width={16} sx={{ color: SECTION_ICON_COLOR }} />
                        <Typography sx={SECTION_HEADER_LABEL}>Contact method</Typography>
                    </Box>
                    <Box sx={{ ...CARD, display: 'flex', gap: 1 }}>
                        <Box onClick={() => setCalledConfirmed(true)} sx={CHIP(calledConfirmed === true, false)}>
                            <Iconify icon="eva:phone-fill" width={16} sx={{ mr: 0.75 }} />
                            Yes, I called
                        </Box>
                        <Box onClick={() => setCalledConfirmed(false)} sx={CHIP(calledConfirmed === false, true)}>
                            <Iconify icon="ri:whatsapp-fill" width={16} sx={{ mr: 0.75 }} />
                            WhatsApp only
                        </Box>
                    </Box>
                    {/* Call outcome — only when "Yes, I called" */}
                    {calledConfirmed === true && (
                        <Box sx={{ ...CARD, mt: 1 }}>
                            <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Leave a note about the call attempt</Typography>
                            <TextField size="small" fullWidth multiline rows={2} value={callNote} onChange={(e) => setCallNote(e.target.value)}
                                placeholder="Enter or select details about the call attempt"
                                sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '12px' } }} />
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {['No answer', 'Left voicemail', 'Incorrect number', 'Call declined', 'Poor call quality', 'Reached different person', 'Busy line', 'Requested call-back'].map((opt) => (
                                    <Box key={opt} onClick={() => {
                                        const newSelected = selectedCallOptions.includes(opt) ? selectedCallOptions.filter(v => v !== opt) : [...selectedCallOptions, opt];
                                        setSelectedCallOptions(newSelected);
                                        setCallNote(newSelected.join(', '));
                                    }} sx={CHIP(selectedCallOptions.includes(opt), false)}>{opt}</Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Step 2: Reminder */}
                <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                        <Box sx={BADGE}>2</Box>
                        <Iconify icon="eva:bell-fill" width={16} sx={{ color: SECTION_ICON_COLOR }} />
                        <Typography sx={SECTION_HEADER_LABEL}>Follow up reminder</Typography>
                    </Box>
                    <Box sx={CARD}>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'nowrap' }}>
                        {REMINDER_PRESETS.map((preset) => (
                            <Box key={preset.key} onClick={() => setActivePreset(preset.key)} sx={CHIP(activePreset === preset.key, preset.key === 'none')}>
                                {preset.key === 'none' && <Iconify icon="mdi:bell-off-outline" width={14} sx={{ mr: 0.5 }} />}
                                {preset.label}
                            </Box>
                        ))}
                    </Box>

                    {/* Custom date picker */}
                    {activePreset === 'custom' && (
                        <Box sx={{ mt: 2, maxWidth: 320 }}>
                            <Controller name="reminderDateTime" control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <DateTimePicker
                                        label="Date & time"
                                        value={field.value || null}
                                        format="dd/MM/yyyy HH:mm"
                                        ampm={false}
                                        minDateTime={new Date()}
                                        onChange={(v) => field.onChange(v)}
                                        slotProps={{ textField: { fullWidth: true, size: 'small', error: !!error, helperText: error?.message, sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } } } }}
                                    />
                                )}
                            />
                        </Box>
                    )}

                    {/* Method + Assignees */}
                    {activePreset && activePreset !== 'none' && (
                        <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'flex-start' }}>
                            <Box sx={{ width: 200 }}>
                                <RHFSelect name="reminder" label="Method" size="small"
                                    SelectProps={{ native: false, sx: { textTransform: 'capitalize', borderRadius: '10px' } }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                                    {(reminderMethod || []).map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value} sx={selectMenuItemStyle}>{opt.label}</MenuItem>
                                    ))}
                                </RHFSelect>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <AssignedToLead label="Assign to" assignToState={assignToState} setAssignTo={setAssignToState} />
                            </Box>
                        </Box>
                    )}
                    </Box>
                </Box>

                {/* Step 3: Notes */}
                <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                        <Box sx={BADGE}>3</Box>
                        <Iconify icon="eva:edit-2-fill" width={16} sx={{ color: SECTION_ICON_COLOR }} />
                        <Typography sx={SECTION_HEADER_LABEL}>Notes</Typography>
                    </Box>
                    <Box sx={CARD}>
                    <RHFTextField name="notes" multiline rows={3} placeholder="Add a note..." InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px' } }} />
                    </Box>
                </Box>
            </Box>
        </FormProvider>
    );
});

WhatsAppFollowUpStep.propTypes = {
    handleNext: PropTypes.func,
    handleClose: PropTypes.func,
    onWhatsAppClose: PropTypes.func,
};

export default WhatsAppFollowUpStep;
